import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Info } from 'lucide-react';

/**
 * An info icon whose explanation appears on hover or keyboard focus.
 *
 * For text that is worth having but crowds the thing it explains — a paragraph
 * of why a VAT default is not always the safe one, or why LedgerLoop refuses to
 * compute an OCR number.
 *
 * Not the native `title` attribute: browsers delay it about a second, render it
 * outside the page, and truncate a paragraph, so in practice the text never
 * appears — which is exactly how this started (the settings hints showed only
 * the help cursor). OverlayTrigger is what the rest of the app uses and renders
 * in the DOM.
 *
 * Bootstrap caps `.tooltip-inner` at 200px, and the cap sits on the inner
 * element, so a width set on the content inside it does nothing — hence the
 * class rule below. It is injected into the head once rather than rendered per
 * instance, since a page may hold half a dozen hints.
 */
const STYLE_ID = 'info-hint-styles';
const STYLE = `
  .info-hint-tooltip .tooltip-inner {
    max-width: 22rem;
    text-align: left;
    padding: 0.5rem 0.65rem;
    line-height: 1.4;
  }
`;

// React 17 here, so no useId — a module counter gives each hint a stable id for
// aria-describedby across re-renders.
let seq = 0;

const InfoHint = ({ text, placement = 'top', size = 14 }) => {
    const [id] = useState(() => `info-hint-${++seq}`);

    useEffect(() => {
        if (document.getElementById(STYLE_ID)) return;
        const el = document.createElement('style');
        el.id = STYLE_ID;
        el.textContent = STYLE;
        document.head.appendChild(el);
    }, []);

    return (
        <OverlayTrigger
            placement={placement}
            trigger={['hover', 'focus']}
            overlay={<Tooltip id={id} className="info-hint-tooltip">{text}</Tooltip>}
        >
            <span
                className="ms-1 text-muted"
                style={{ cursor: 'help', display: 'inline-flex', verticalAlign: 'middle' }}
                aria-label={text}
                tabIndex={0}
                role="note"
            >
                <Info size={size} />
            </span>
        </OverlayTrigger>
    );
};

export default InfoHint;
